import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSearchContext } from '@/contexts/SearchContext';
import { Button } from '@/components/ui/Button';
import PromptCard from '@/components/PromptCard';
import PromptFormModal from '@/components/PromptFormModal';
import { onAuthRequired } from '../utils/auth';
import { API_URLS } from '../constants';
import { Sidebar } from './ui';
import { enhancePrompt } from '@/utils/promptAgent';
import { authenticatedFetch } from '@/utils/api';

export interface Prompt {
  id: string;
  title: string;
  description: string;
  votes: number;
  posted_at?: string;
  categories?: string[];
  total_views: number;
  total_copies: number;
  prompt_type: 'user' | 'admin';
  user_id?: string;
  is_public?: boolean;
  created_at?: string;
  modified_at?: string;
  category_id?: number;
  collection_ids?: number[];
  author_name?: string;
  author_profile_picture?: string;
}

interface MainContentProps {
  selectedCategory: string | null;
  setSelectedCategory: (value: string) => void;
  selectedCollectionId?: string | null;
  showMyPrompts?: boolean;
}

export const MainContent: React.FC<MainContentProps> = ({ selectedCategory, setSelectedCategory, selectedCollectionId, showMyPrompts }) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchResults, setSearchResults } = useSearchContext();

  // Enhancement state
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasBeenEnhanced, setHasBeenEnhanced] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState<string>('');
  const [enhancedPrompt, setEnhancedPrompt] = useState<string>('');
  // Only use search results when no explicit filter is active
  const isFilterActive = !!(showMyPrompts || selectedCollectionId || (selectedCategory && selectedCategory !== 'All'));
  const displayedPrompts = (!isFilterActive && searchResults && searchResults.length > 0) ? searchResults : prompts;

  const handleCreatePrompt = useCallback(() => {
    if (!isLoggedIn) {
      onAuthRequired();
      return;
    }
    setIsModalOpen(true);
  }, [isLoggedIn]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Reset enhancement state when modal closes
    setIsEnhanced(false);
    setOriginalPrompt('');
    setEnhancementError(null);
    setHasBeenEnhanced(false);
    setEnhancedPrompt('');
  }, []);

  const handleEnhancePrompt = useCallback(async (promptText: string): Promise<string> => {
    if (!promptText.trim()) {
      setEnhancementError('Please enter a prompt to enhance');
      throw new Error('Please enter a prompt to enhance');
    }

    // If this text has already been enhanced, return the cached enhanced version
    if (hasBeenEnhanced && originalPrompt === promptText.trim() && enhancedPrompt) {
      setIsEnhanced(true);
      return enhancedPrompt;
    }

    setIsEnhancing(true);
    setEnhancementError(null);

    try {
      // Store original prompt before enhancement
      setOriginalPrompt(promptText.trim());
      const enhanced = await enhancePrompt(promptText);
      setEnhancedPrompt(enhanced);
      setIsEnhanced(true);
      setHasBeenEnhanced(true);
      return enhanced;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to enhance prompt';
      setEnhancementError(errorMessage);
      throw error;
    } finally {
      setIsEnhancing(false);
    }
  }, [hasBeenEnhanced, originalPrompt, enhancedPrompt]);

  const handleRevertToOriginal = useCallback(() => {
    setIsEnhanced(false);
    setEnhancementError(null);
    // Return the original prompt text so the modal can update its state
    // Keep hasBeenEnhanced and cached texts for potential re-toggle
    return originalPrompt;
  }, [originalPrompt]);

  const handleResetEnhancementState = useCallback(() => {
    // Reset all enhancement state when user manually edits the text
    setIsEnhanced(false);
    setEnhancementError(null);
    setHasBeenEnhanced(false);
    setOriginalPrompt('');
    setEnhancedPrompt('');
  }, []);

  const handleToggleToEnhanced = useCallback(() => {
    // Toggle back to enhanced version (no API call needed)
    if (hasBeenEnhanced && enhancedPrompt) {
      setIsEnhanced(true);
      setEnhancementError(null);
      return enhancedPrompt;
    }
    return '';
  }, [hasBeenEnhanced, enhancedPrompt]);

  const fetchPrompts = useCallback(async () => {
    setIsLoading(true);
    try {
      let apiUrl: string;

      if (showMyPrompts) {
        apiUrl = '/api/prompts/my-prompts';
      } else if (selectedCollectionId) {
        apiUrl = `/api/collections/${selectedCollectionId}/prompts`;
      } else if (selectedCategory && selectedCategory !== 'All') {
        apiUrl = API_URLS.PROMPTS.CATEGORY(selectedCategory);
      } else {
        apiUrl = API_URLS.PROMPTS.LIST;
      }

      const response = await authenticatedFetch(apiUrl);

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in to view prompts.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to view prompts.');
        } else {
          throw new Error(`Error fetching prompts: ${response.statusText} (${response.status})`);
        }
      }

      const data = await response.json();
      // Handle different response formats - collections API returns data in 'data' property
      const promptsData = selectedCollectionId ? data.data : data;
      setPrompts(promptsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch prompts');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, selectedCollectionId, showMyPrompts]);

  const handleSubmitPrompt = useCallback(async (data: { title: string; description: string; full_text: string; }) => {
    try {
      const res = await authenticatedFetch(API_URLS.PROMPTS.CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to create prompt');
      }
      const newPrompt = await res.json();
      setIsModalOpen(false);
      // Optionally refresh the list
      fetchPrompts();
      // Redirect to detail view
      if (newPrompt && newPrompt.id) {
        navigate(`/prompts/${newPrompt.id}`);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create prompt');
    }
  }, [fetchPrompts, navigate]);

  // Initial load and when filters change
  useEffect(() => {
    setSearchResults([]);
    fetchPrompts();
  }, [selectedCategory, selectedCollectionId, showMyPrompts, fetchPrompts, setSearchResults]);

  // Current frontend version timestamp for tracking deployment
  const frontendVersion = "v1.0.1-2025-06-13-1416";

  return (
    <div className='flex flex-col w-full h-full'>
      <header className="shrink-0 py-6 lg:py-8 px-4 lg:px-8">
        <div className="flex justify-between items-center mb-8 lg:mb-12 max-w-7xl mx-auto">
          <div className="flex items-baseline gap-2 md:gap-4 lg:gap-6">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 tracking-tight text-left">Discover</h1>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 font-medium hidden md:block text-left">
              Top AI Prompts - Curated. Shared. Put to Work.
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-brand-dark-blue hover:bg-brand-electric-blue text-white rounded-lg lg:hidden font-medium transition-colors duration-200 text-sm md:text-base"
              onClick={() => setIsSidebarOpen(true)}
            >
              Categories <span className="text-xs md:text-sm">▼</span>
            </button>
            <Button
              onClick={handleCreatePrompt}
              className="shrink-0 px-4 md:px-5 py-2 md:py-2.5 font-medium text-sm md:text-base"
              data-testid="create-prompt-button"
            >
              Create Prompt
            </Button>
          </div>
          {isSidebarOpen && (
            <Sidebar
              isLoggedIn={isLoggedIn}
              isOpen={true}
              onClose={() => setIsSidebarOpen(false)}
              collections={[]}
              selectedCollectionId={null}
              onSelectCollection={() => { }}
              onShowCreateCollectionModal={() => { }}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
          )}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-4">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400"></div>
              <span className="ml-3 text-gray-600">
                {showMyPrompts ? 'Loading your prompts...' : 
                 selectedCollectionId ? 'Loading collection prompts...' :
                 selectedCategory && selectedCategory !== 'All' ? `Loading ${selectedCategory} prompts...` :
                 'Loading prompts...'}
              </span>
            </div>
          ) : displayedPrompts && displayedPrompts.length > 0 ? (
            displayedPrompts.map((prompt) => {
              const categoryTags = Array.isArray(prompt.categories) ? prompt.categories : [];
              const shouldForceSelectedCategory = selectedCategory && selectedCategory !== 'All';
              const tagsToShow = shouldForceSelectedCategory && categoryTags.includes(selectedCategory!)
                ? [selectedCategory!]
                : categoryTags;

              return (
                <PromptCard
                  key={prompt.id}
                  id={prompt.id}
                  title={prompt.title}
                  description={prompt.description}
                  votes={prompt.votes}
                  total_views={prompt.total_views}
                  tags={tagsToShow}
                  total_copies={prompt.total_copies}
                  promptType={prompt.prompt_type}
                  collectionIds={prompt.collection_ids}
                  author_name={prompt.author_name}
                  author_profile_picture={prompt.author_profile_picture}
                />
              );
            })
          ) : (
            <div className="text-center py-10 text-gray-500">
              {showMyPrompts ? "You haven't created any prompts yet. Create your first one!" :
               selectedCollectionId ? "This collection is empty. Add some prompts!" :
               selectedCategory && selectedCategory !== 'All' ? `No prompts found in ${selectedCategory} category.` :
               "No prompts found. Be the first to create one!"}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 pt-5 border-t text-xs text-gray-500 text-center shrink-0">
        Frontend Version: {frontendVersion} | API Status: {error ? "❌ Error" : "✅ Connected"}
      </div>

      <PromptFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPrompt}
        onEnhancePrompt={handleEnhancePrompt}
        isEnhancing={isEnhancing}
        enhancementError={enhancementError}
        isEnhanced={isEnhanced}
        hasBeenEnhanced={hasBeenEnhanced}
        onRevertToOriginal={handleRevertToOriginal}
        onToggleToEnhanced={handleToggleToEnhanced}
        onResetEnhancementState={handleResetEnhancementState}
      />
    </div>
  );
};
